"use client";

import { defaultSupportAgentId } from "@/constants";
import { convAILanguages } from "@mw/shared/languagesAndAccents";
import { getLocalStorage, setLocalStorage, LocalStorageKeys } from "@utilities/localStorage";
import { useState } from "react";

import { useCallManager } from "./CallManager";
import { ExpandedView } from "./InCall";
import { InlineTerms } from "./InlineTerms";

export function InlineAgent() {
  // TODO(agent-controller-refactor): migrate duplicated terms/session orchestration to shared controller.
  const [showTerms, setShowTerms] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [state, actions] = useCallManager(defaultSupportAgentId, { variant: "inline" });

  // Handle language change
  const handleLanguageChange = (languageValue: string): void => {
    const newLanguage = convAILanguages.find(l => l.value === languageValue);
    if (newLanguage) {
      actions.setLanguage(newLanguage);
    }
  };

  const handleAgreeToTerms = async () => {
    setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
    setShowTerms(false);
    setIsStarting(true);
    await actions.startCall();
    setIsStarting(false);
  };

  const handleCancelTerms = () => {
    // Just hide terms without starting call
    setShowTerms(false);
  };

  const handleStartCall = async () => {
    // Check if terms are accepted before starting call
    const currentTermsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (currentTermsAccepted) {
      setIsStarting(true);
      await actions.startCall();
      setIsStarting(false);
    } else {
      setShowTerms(true);
    }
  };

  if (showTerms) {
    return (
      <div className='flex w-full items-center justify-center'>
        <div
          className='flex w-full max-w-[400px] flex-col items-center rounded-[30px] bg-default p-24'
          style={{
            boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.1)",
            minHeight: "440px",
            borderRadius: "30px",
          }}
        >
          <InlineTerms onAgree={handleAgreeToTerms} onCancel={handleCancelTerms} />
        </div>
      </div>
    );
  }

  return (
    <ExpandedView
      elapsed={state.elapsed}
      getInputVolume={state.getInputVolume}
      getOutputVolume={state.getOutputVolume}
      isStarting={isStarting}
      language={state.language}
      muted={state.muted}
      onEndCall={async () => await actions.endCall()}
      onLanguageChange={handleLanguageChange}
      onStartCall={handleStartCall}
      onToggleMute={() => actions.setMuted(!state.muted)}
      status={state.status}
      variant='inline'
    />
  );
}
